import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { TeamTimelineService } from '../instances/team-timeline';
import { TeamTimelineConverter } from '@/converters';

export const useTimeline = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_TIMELINE', params],
    async () => {
      const [err, data] = await TeamTimelineService.getTeamTimeline(params);
      if (err) {
        throw err;
      }
      return {
        data: TeamTimelineConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useTimelineById = (id: string) => {
  const query = useQuery(
    ['GET_TIMELINE_BY_ID', id],
    async () => {
      const [err, data] = await TeamTimelineService.getTeamTimelineById(id);
      if (err) {
        throw err;
      }
      return TeamTimelineConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostTimelineMutation = () => {
  return useMutation({
    mutationKey: ['POST_TIMELINE'],
    mutationFn: async (payload: any) => {
      const [err, data] = await TeamTimelineService.postTeamTimeline(payload);
      if (err) {
        throw err;
      }
      return TeamTimelineConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutTimelineMutation = () => {
  return useMutation({
    mutationKey: ['PUT_TIMELINE'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await TeamTimelineService.putTeamTimeline(id, payload);
      if (err) {
        throw err;
      }
      return TeamTimelineConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteTimelineMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_TIMELINE'],
    mutationFn: async (id: string) => {
      const [err] = await TeamTimelineService.deleteTeamTimeline(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetTimelineById = () => {
  return useMutation({
    mutationKey: ['GET_TIMELINE_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await TeamTimelineService.getTeamTimelineById(uuid);
      if (err) {
        throw err;
      }
      return TeamTimelineConverter.toDomainFromByIdResponse(data);
    },
  });
};
